import { Button, Dialog, DialogActions, DialogContent, Grid2 } from '@mui/material'
import { NewTable } from 'components/datatable/NewTable'
import React, { useEffect, useRef, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { useDispatch } from 'react-redux'
import IntegrationAction from 'import/actions/IntegrationAction'
import DtoPARParameters from 'survey/dto/DtoPARParameters'
import DtoFileData from 'import/dto/DtoFileData'
import { zipObject, keys, pickBy, isUndefined, mapValues } from 'lodash'
import { nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import Checkbox from 'components/forms/Checkbox'
import { Close, Done } from '@mui/icons-material'
import JobAction from 'import/actions/JobAction'
import { getLogin } from 'utils/SettingUtils'
import ToastrAction from 'toastr/actions/ToastrAction'
import { hasValue } from 'utils/NumberUtil'
import RefJobAction from 'domain/job/RefJobAction'
import ProgressBar from 'components/progress/ProgressBar'
import { PAR_CONSTANTS } from 'agriAdministration/constants/AgriConstants'

const PARModalCheck = ({ open, onClose, importParameters, onEndJob }) => {
    const [overviewDatas, setOverviewDatas] = useState([])
    const [suppressPreviousIntegration, setSuppressPreviousIntegration] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [jobIsRunning, setJobIsRunning] = useState(false)

    const dispatch = useDispatch()

    const headers = keys(pickBy(importParameters.columnsValues, val => !!val)).map(key => i18n[PAR_CONSTANTS[key].i18n])

    const formatDatas = (datas) => {
        const fileDatas = datas.map(tab => tab.map(data => new DtoFileData(data)))
        const formatedDatas = fileDatas.map(row => {
            return mapValues(zipObject(headers, row), v => v?.stringValue)
        })
        setIsLoading(false)
        setOverviewDatas(formatedDatas)
    }

    const timeoutRef = useRef()

    const onCloseHandler = () => {
        onClose()
        if (jobIsRunning) {
            dispatch(ToastrAction.info(`${i18n.jobProgressMessage}\n${i18n.jobDetailsMessage}`))
            clearTimeout(timeoutRef.current)
            onEndJob()
        }
    }

    useEffect(() => {
        dispatch(IntegrationAction.getAgriParPreview(importParameters)).then(res => {
            if (res) {
                formatDatas(res)
            } else {
                onClose()
            }
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const automaticCallLogs = (id) => {
        RefJobAction.promiseJobExecutions(-14, 10).then(res => {
            const jobExecution = res.find(e => e.id === id)
            if (isUndefined(jobExecution?.statusCode)) {
                timeoutRef.current = (setTimeout(() => automaticCallLogs(id), 2000))
                return
            }
            if ([1, 2].includes(jobExecution?.statusCode)) dispatch(ToastrAction.success(i18n.jobSuccessMessage))
            else dispatch(ToastrAction.error(i18n.jobFailedMessage))
            setJobIsRunning(false)
            onClose()
            onEndJob()
        })
    }

    const onValidate = () => {
        JobAction.promiseJob('-14').then(job => {
            const oldParams = JSON.parse(job.parameters)
            const newJob = {
                ...job, parameters: {
                    jobType: oldParams.jobType,
                    routingKey: oldParams.routingKey,
                    filters: [JSON.stringify({ ...importParameters, columnsValues: mapValues(importParameters.columnsValues, value => value - 1), supPreviousIntegration: suppressPreviousIntegration })],
                }, login: getLogin(),
            }
            JobAction.promiseUpdateJob(newJob).then(res => {
                if (res.status !== 200) {
                    dispatch(ToastrAction.error(i18n.updateError + i18n.jobs))
                } else {
                    JobAction.promiseExecuteJob(newJob.id).then(json => {
                        if (hasValue(json.execution)) {
                            setIsLoading(true)
                            setJobIsRunning(true)
                            automaticCallLogs(json.execution)
                            return
                        }
                        setTimeout(() => JobAction.promiseExecuteJob(newJob.id).then(json2 => {
                            if (hasValue(json2.execution)) {
                                setIsLoading(true)
                                setJobIsRunning(true)
                                automaticCallLogs(json.execution)
                                return
                            }
                            throw new Error('Impossible to launch Job')
                        }), 5000)
                    })
                }
            })
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onCloseHandler}
            maxWidth={'xl'}
        >
            <DefaultDialogTitle title={i18n.importedDatasOverview} onClose={onClose} />
            <DialogContent>
                {isLoading || jobIsRunning ?
                    <ProgressBar indeterminate withMessage message={jobIsRunning ? i18n.jobProgressMessage : undefined} />
                    :
                    <NewTable rows={overviewDatas} headers={headers} rowsPerPageOptions={nbPerPageLabelMedium} />}
            </DialogContent>
            {!isLoading && !jobIsRunning && (
                <DialogActions>
                    <Grid2 container size={12} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%' }}>
                        <Grid2 size={4}>
                            <Checkbox
                                label={i18n.deletePreviousImportedUses}
                                checked={suppressPreviousIntegration}
                                onChange={(e) => setSuppressPreviousIntegration(e)}
                            />
                        </Grid2>
                        {!headers.some(header => header.includes('Volume')) && <Grid2 size={4} sx={{ color: 'errorStatus.contrastText', textAlign: 'center' }}>{i18n.goingToImportNoVolumes}</Grid2>}
                        <Grid2 container size={4} spacing={2} justifyContent={'flex-end'} alignItems={'center'}>
                            <Button
                                variant='outlined'
                                startIcon={<Close />}
                                sx={{ color: 'primary.main' }}
                                onClick={onClose}
                            >
                                {i18n.cancel}
                            </Button>
                            <Button
                                endIcon={<Done />}
                                sx={{ color: 'white', backgroundColor: 'primary.main', padding: '5px 15px' }}
                                onClick={onValidate}
                            >
                                {i18n.validate}
                            </Button>
                        </Grid2>
                    </Grid2>
                </DialogActions>
            )}
        </Dialog>
    )
}

PARModalCheck.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    importParameters: PropTypes.instanceOf(DtoPARParameters),
    onEndJob: PropTypes.func.isRequired,
}

export default PARModalCheck